import { useCallback, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import baseUrl from "../../../api/baseUrl";
import socket from "../../../api/socket";
import CloseIcon from "@mui/icons-material/Close";
import FlagIcon from "@mui/icons-material/Flag";

interface AddBrandProps {
  closeModal: () => void;
  id?: string;
}

interface FormData {
  purposeName: string;
}

const AddPurpose: React.FC<AddBrandProps> = ({ closeModal, id }) => {
  const methods = useForm<FormData>();

  const getBrandData = useCallback(
    (purposeid: string) => {
      baseUrl
        .get("client/setting/purpose/findPurposeById", {
          headers: { purposeid: purposeid },
        })
        .then((response) => {
          methods.reset(response.data.data);
        })
        .catch((error) => {
          console.error(error?.response);
        });
    },
    [methods]
  );

  useEffect(() => {
    if (id) {
      getBrandData(id);
    }
  }, [id, getBrandData]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) => {
      const apiCall = id
        ? baseUrl.patch("client/setting/purpose/updatePurposeById", data, {
            headers: { purposeid: id },
          })
        : baseUrl.post("client/setting/purpose/createPurpose", data);

      apiCall
        .then((response) => {
          if (!id) socket.emit("addpurpose", response.data.data);
          closeModal();
        })
        .catch((error) => {
          console.error(error?.response);
        });
    },
    [closeModal, id]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlagIcon className="text-white" sx={{ fontSize: 24 }} />
            <h2 className="text-lg font-bold text-white">
              {id ? "Update Purpose" : "Add Purpose"}
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Form */}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="p-6">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Purpose Name <span className="text-red-500">*</span>
              </label>
              <input
                {...methods.register("purposeName", { required: true })}
                type="text"
                placeholder="Enter purpose name"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-5 py-2.5 text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:shadow-lg hover:shadow-amber-200 transition-all font-medium"
              >
                {id ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddPurpose;
